/**
 * Pure edits of an {@link ObjectDefinitionSpec} — every editor action is one
 * of these (spec in, new spec out), so the store can keep undo history as
 * plain snapshots and the operations are unit-testable without a UI.
 *
 * Invariants kept here (the validator would reject violations):
 *  - a shape belongs to at most one group; empty groups are dropped;
 *  - param references (repeat pitch/count, visibleWhen, modifier bindings)
 *    follow renames and fall back to constants / disappear when a param is
 *    removed.
 */

import {
  isFormulaHandlePoint,
  type NumberBinding,
  type ObjectAxisResize,
  type ObjectDefinitionSpec,
  type ObjectGroup,
  type ObjectHandlePoint,
  type ObjectLinearRepeat,
  type ObjectModifier,
  type ObjectParam,
  type ObjectParamAssignment,
  type ObjectRadialRepeat,
  type ObjectRepeat,
  type ObjectShape,
  type Vec2,
} from 'shared-types';
import { shapesBounds, tessellateObjectShape } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';

type Spec = ObjectDefinitionSpec;

const randomId = (prefix: string): string => `${prefix}${Math.random().toString(36).slice(2, 9)}`;
export const newShapeId = (): string => randomId('s_');
export const newGroupId = (): string => randomId('g_');

/** Round to 0.1 mm: keeps the stored JSON readable and diff-stable. */
export const roundCoord = (v: number): number => Math.round(v * 1e4) / 1e4;
const roundVec = (p: Vec2): Vec2 => [roundCoord(p[0]), roundCoord(p[1])];

// ─── Shapes ─────────────────────────────────────────────────────────────

export function addShapes(spec: Spec, shapes: readonly ObjectShape[]): Spec {
  return { ...spec, shapes: [...spec.shapes, ...shapes] };
}

export function updateShape(spec: Spec, id: string, update: (shape: ObjectShape) => ObjectShape): Spec {
  return { ...spec, shapes: spec.shapes.map((s) => (s.id === id ? update(s) : s)) };
}

/** Remove shapes (and them from their groups; groups left empty go too). */
export function removeShapes(spec: Spec, ids: readonly string[]): Spec {
  const gone = new Set(ids);
  return {
    ...spec,
    shapes: spec.shapes.filter((s) => !gone.has(s.id)),
    groups: spec.groups.map((g) => ({ ...g, shapeIds: g.shapeIds.filter((id) => !gone.has(id)) })).filter((g) => g.shapeIds.length > 0),
  };
}

/** Map every coordinate of a shape through `f` (radii scale with `radiusScale`). */
export function mapShape(shape: ObjectShape, f: (p: Vec2) => Vec2, radiusScale = 1): ObjectShape {
  switch (shape.kind) {
    case 'polyline':
      return { ...shape, points: shape.points.map((p) => roundVec(f(p))) };
    case 'rect': {
      const a = f(shape.min);
      const b = f(shape.max);
      return { ...shape, min: roundVec([Math.min(a[0], b[0]), Math.min(a[1], b[1])]), max: roundVec([Math.max(a[0], b[0]), Math.max(a[1], b[1])]) };
    }
    case 'circle':
      return { ...shape, center: roundVec(f(shape.center)), radius: roundCoord(shape.radius * radiusScale) };
    case 'arc':
      return { ...shape, center: roundVec(f(shape.center)), radius: roundCoord(shape.radius * radiusScale) };
    case 'path':
      // Control points are absolute: an affine map of the points is the map of the curve.
      return { ...shape, nodes: shape.nodes.map((n) => ({ point: roundVec(f(n.point)), ...(n.in ? { in: roundVec(f(n.in)) } : {}), ...(n.out ? { out: roundVec(f(n.out)) } : {}) })) };
  }
}

/** A line or rectangle as a bezier path with sharp corners (to bend it with the pen tool's handles). */
export function toPath(shape: ObjectShape): ObjectShape {
  if (shape.kind !== 'polyline' && shape.kind !== 'rect') return shape;
  const points: Vec2[] = shape.kind === 'polyline' ? [...shape.points] : [shape.min, [shape.max[0], shape.min[1]], shape.max, [shape.min[0], shape.max[1]]];
  const { id, name, style, layer, hidden } = shape;
  return { id, kind: 'path', nodes: points.map((point) => ({ point })), closed: shape.kind === 'rect' || shape.closed, ...(name ? { name } : {}), ...(style ? { style } : {}), ...(layer ? { layer } : {}), ...(hidden ? { hidden } : {}) };
}

export function translateShapes(spec: Spec, ids: readonly string[], du: number, dn: number): Spec {
  const moving = new Set(ids);
  return { ...spec, shapes: spec.shapes.map((s) => (moving.has(s.id) ? mapShape(s, (p) => [p[0] + du, p[1] + dn]) : s)) };
}

/**
 * Mirror shapes across the vertical (u) or horizontal (n) line through the
 * center of their joint bounds. Arcs mirror their angles too.
 */
export function mirrorShapes(spec: Spec, ids: readonly string[], axis: 'u' | 'n'): Spec {
  const selected = spec.shapes.filter((s) => ids.includes(s.id));
  const bu = shapesBounds(selected, 0);
  const bn = shapesBounds(selected, 1);
  const cu = (bu.min + bu.max) / 2;
  const cn = (bn.min + bn.max) / 2;
  const f = (p: Vec2): Vec2 => (axis === 'u' ? [2 * cu - p[0], p[1]] : [p[0], 2 * cn - p[1]]);
  return {
    ...spec,
    shapes: spec.shapes.map((s) => {
      if (!ids.includes(s.id)) return s;
      const mapped = mapShape(s, f);
      if (mapped.kind !== 'arc') return mapped;
      // Mirroring across u flips angles around π/2; across n negates them.
      const flip = (a: number): number => (axis === 'u' ? Math.PI - a : -a);
      return { ...mapped, startAngle: flip(mapped.startAngle), endAngle: flip(mapped.endAngle) };
    }),
  };
}

/** Copies of `ids` offset by (du, dn); returns the new spec and the copies' ids (in order). */
export function duplicateShapes(spec: Spec, ids: readonly string[], du: number, dn: number): { spec: Spec; ids: string[] } {
  const copies = spec.shapes.filter((s) => ids.includes(s.id)).map((s) => ({ ...mapShape(s, (p) => [p[0] + du, p[1] + dn]), id: newShapeId() }));
  return { spec: addShapes(spec, copies), ids: copies.map((c) => c.id) };
}

// ─── Groups ─────────────────────────────────────────────────────────────

export function groupOfShape(spec: Spec, shapeId: string): ObjectGroup | null {
  return spec.groups.find((g) => g.shapeIds.includes(shapeId)) ?? null;
}

/** Group shapes (taking them out of their current groups); returns the new group's id. */
export function groupShapes(spec: Spec, ids: readonly string[], name: string): { spec: Spec; groupId: string } {
  const groupId = newGroupId();
  const moving = new Set(ids);
  const others = spec.groups.map((g) => ({ ...g, shapeIds: g.shapeIds.filter((id) => !moving.has(id)) })).filter((g) => g.shapeIds.length > 0);
  const group: ObjectGroup = { id: groupId, name, shapeIds: [...ids], resize: { u: 'stretch', n: 'stretch' } };
  return { spec: { ...spec, groups: [...others, group] }, groupId };
}

export function ungroup(spec: Spec, groupId: string): Spec {
  return { ...spec, groups: spec.groups.filter((g) => g.id !== groupId) };
}

export function updateGroup(spec: Spec, groupId: string, update: (group: ObjectGroup) => ObjectGroup): Spec {
  return { ...spec, groups: spec.groups.map((g) => (g.id === groupId ? update(g) : g)) };
}

// ─── Modifiers ──────────────────────────────────────────────────────────

/** Add a modifier to the group's stack (at the end). */
export function addModifier(spec: Spec, groupId: string, modifier: ObjectModifier): Spec {
  return updateGroup(spec, groupId, (g) => ({ ...g, modifiers: [...(g.modifiers ?? []), modifier] }));
}

/** What the modifier stack's Add menu offers: a repeat (one per group, always first) or a modifier. */
export type ModifierChoice = 'repeatLinear' | 'repeatRadial' | 'mirror' | 'roundCorners';

/** Add a stack entry: a repeat (replacing none — a group has at most one) or a modifier with its defaults. */
export function addModifierChoice(spec: Spec, groupId: string, choice: ModifierChoice): Spec {
  const group = spec.groups.find((g) => g.id === groupId);
  if (!group) return spec;
  if (choice === 'repeatLinear' || choice === 'repeatRadial') {
    if (group.repeat) return spec;
    return updateGroup(spec, groupId, (g) => ({ ...g, repeat: choice === 'repeatLinear' ? defaultLinearRepeat(spec, g, 'u') : defaultRadialRepeat() }));
  }
  return addModifier(spec, groupId, choice === 'mirror' ? { kind: 'mirror', axis: 'u' } : { kind: 'roundCorners', radius: { value: 0.05 } });
}

/** Remove the group's repeat (its copies go; the drawn cell stays). */
export function removeRepeat(spec: Spec, groupId: string): Spec {
  return updateGroup(spec, groupId, (g) => {
    const { repeat: _drop, ...rest } = g;
    return rest;
  });
}

/** Replace, move or remove one modifier of a group (null removes it; `move` shifts it up −1 / down +1). */
export function updateModifier(spec: Spec, groupId: string, index: number, next: ObjectModifier | null, move = 0): Spec {
  return updateGroup(spec, groupId, (g) => {
    const list = [...(g.modifiers ?? [])];
    if (index < 0 || index >= list.length) return g;
    if (next === null) list.splice(index, 1);
    else list[index] = next;
    const target = index + move;
    if (next !== null && move !== 0 && target >= 0 && target < list.length) [list[index], list[target]] = [list[target], list[index]];
    const { modifiers: _old, ...rest } = g;
    return list.length > 0 ? { ...rest, modifiers: list } : rest;
  });
}

/** Wrap loose shapes in a group of their own (the modifier stack lives on groups); a whole group stays as it is. */
export function groupForModifiers(spec: Spec, ids: readonly string[], name: string): { spec: Spec; groupId: string } | null {
  if (ids.length === 0) return null;
  const whole = spec.groups.find((g) => g.shapeIds.length === ids.length && ids.every((id) => g.shapeIds.includes(id)));
  return whole ? { spec, groupId: whole.id } : groupShapes(spec, ids, name);
}

const negate = (src: string): string => `-(${src})`;

/** A shape mirrored across the object's center: coordinates, arc angles and formulas alike. */
function mirrorShapeAboutCenter(shape: ObjectShape, su: number, sn: number): ObjectShape {
  const mapped = mapShape(shape, (p) => [p[0] * su, p[1] * sn]);
  const turned: ObjectShape =
    mapped.kind === 'arc'
      ? {
          ...mapped,
          // (cos a, sin a) → (su cos a, sn sin a): mirroring u turns a into π − a, mirroring n into −a.
          startAngle: su < 0 ? (sn < 0 ? mapped.startAngle + Math.PI : Math.PI - mapped.startAngle) : sn < 0 ? -mapped.startAngle : mapped.startAngle,
          endAngle: su < 0 ? (sn < 0 ? mapped.endAngle + Math.PI : Math.PI - mapped.endAngle) : sn < 0 ? -mapped.endAngle : mapped.endAngle,
        }
      : mapped;
  if (!shape.expr) return turned;
  const expr: Record<string, string> = {};
  const signOf = (axis: string): number => (axis === 'u' ? su : sn);
  for (const [field, src] of Object.entries(shape.expr)) {
    const axis = field.slice(-1);
    if (field === 'startAngle' || field === 'endAngle') expr[field] = su < 0 ? (sn < 0 ? `(${src}) + pi` : `pi - (${src})`) : sn < 0 ? negate(src) : src;
    else if ((axis === 'u' || axis === 'n') && field.includes('.')) {
      // A rect's min and max swap on a mirrored axis (mapShape re-sorts the static corners the same way).
      const mirrored = signOf(axis) < 0;
      const key = mirrored && shape.kind === 'rect' ? (field.startsWith('min.') ? `max.${axis}` : `min.${axis}`) : field;
      expr[key] = mirrored ? negate(src) : src;
    } else expr[field] = src;
  }
  return { ...turned, expr };
}

const swapPins = (rule: ObjectAxisResize): ObjectAxisResize => (rule === 'pinStart' ? 'pinEnd' : rule === 'pinEnd' ? 'pinStart' : rule);

/** A group's repeat mirrored (shapes are mirrored separately); radial turns run the other way. */
function mirrorRepeat(repeat: ObjectRepeat, su: number, sn: number): ObjectRepeat {
  if (repeat.kind === 'index') return repeat;
  if (repeat.kind === 'radial') return { ...repeat, center: [repeat.center[0] * su, repeat.center[1] * sn], ...(su * sn < 0 ? { startAngle: -repeat.startAngle, sweep: -repeat.sweep } : {}) };
  const flip = (repeat.axis === 'u' ? su : sn) < 0;
  if (!flip) return repeat;
  const skip = repeat.skip === 'first' ? 'last' : repeat.skip === 'last' ? 'first' : repeat.skip;
  return {
    ...repeat,
    cell: [-repeat.cell[1], -repeat.cell[0]],
    ...(repeat.run ? { run: [-repeat.run[1], -repeat.run[0]] as const } : {}),
    ...(repeat.runExpr ? { runExpr: { start: negate(repeat.runExpr.end), end: negate(repeat.runExpr.start) } } : {}),
    ...(skip ? { skip } : {}),
  };
}

/**
 * Apply ("bake") a group's mirror modifier: the mirrored copies become real
 * shapes, in a group of their own per copy with the mirrored resize rules
 * (a part pinned to the left end → pinned to the right end), repeat and
 * placement, so the object looks and behaves exactly as before. The rest of
 * the stack stays on both. Round corners can't be baked (the rounding
 * follows the live size).
 */
export function applyMirrorModifier(spec: Spec, groupId: string, index: number): Spec {
  const group = spec.groups.find((g) => g.id === groupId);
  const modifier = group?.modifiers?.[index];
  if (!group || modifier?.kind !== 'mirror') return spec;
  const rest = (group.modifiers ?? []).filter((_, i) => i !== index);
  const { modifiers: _drop, ...base } = group;
  const kept: ObjectGroup = rest.length > 0 ? { ...base, modifiers: rest } : base;
  if (modifier.enabled === false) return updateGroup(spec, groupId, () => kept);
  const signs: ReadonlyArray<readonly [number, number]> = modifier.axis === 'u' ? [[-1, 1]] : modifier.axis === 'n' ? [[1, -1]] : [[-1, 1], [1, -1], [-1, -1]];
  const shapes = spec.shapes.filter((s) => group.shapeIds.includes(s.id));
  const newShapes: ObjectShape[] = [];
  const newGroups: ObjectGroup[] = signs.map(([su, sn]) => {
    const copies = shapes.map((s) => ({ ...mirrorShapeAboutCenter(s, su, sn), id: newShapeId(), ...(s.name ? { name: `${s.name} (mirrored)` } : {}) }));
    newShapes.push(...copies);
    const offset = group.offset
      ? {
          ...(group.offset.u !== undefined ? { u: su < 0 ? negate(group.offset.u) : group.offset.u } : {}),
          ...(group.offset.n !== undefined ? { n: sn < 0 ? negate(group.offset.n) : group.offset.n } : {}),
          ...(group.offset.rotate !== undefined ? { rotate: su * sn < 0 ? negate(group.offset.rotate) : group.offset.rotate } : {}),
        }
      : undefined;
    return {
      ...kept,
      id: newGroupId(),
      name: `${group.name} (mirrored)`,
      shapeIds: copies.map((c) => c.id),
      resize: { ...group.resize, u: su < 0 ? swapPins(group.resize.u) : group.resize.u, n: sn < 0 ? swapPins(group.resize.n) : group.resize.n },
      ...(group.repeat ? { repeat: mirrorRepeat(group.repeat, su, sn) } : {}),
      ...(offset ? { offset } : {}),
    };
  });
  const at = spec.groups.findIndex((g) => g.id === groupId);
  const groups = [...spec.groups.slice(0, at), kept, ...newGroups, ...spec.groups.slice(at + 1)];
  return { ...spec, shapes: [...spec.shapes, ...newShapes], groups };
}

/**
 * A sensible first repeat for a group: cells along `axis` sized to the
 * group's extent (a zero-width group — a single divider line — gets a cell
 * ending at the line, a quarter of the footprint wide).
 */
export function defaultLinearRepeat(spec: Spec, group: ObjectGroup, axis: 'u' | 'n'): ObjectLinearRepeat {
  const shapes = spec.shapes.filter((s) => group.shapeIds.includes(s.id));
  const bounds = shapesBounds(shapes, axis === 'u' ? 0 : 1);
  const extent = axis === 'u' ? spec.defaultSize.width : spec.defaultSize.depth;
  const size = bounds.max - bounds.min;
  const cell: readonly [number, number] = size > 0.01 ? [roundCoord(bounds.min), roundCoord(bounds.max)] : [roundCoord(bounds.max - extent / 4), roundCoord(bounds.max)];
  return { kind: 'linear', axis, cell, pitch: { value: roundCoord(cell[1] - cell[0]) }, fit: 'fill', cellResize: 'stretch' };
}

/** A first radial repeat: copies around the footprint center, a full turn (the editor lets the user move the center). */
export function defaultRadialRepeat(): ObjectRadialRepeat {
  return { kind: 'radial', center: [0, 0], count: { value: 8 }, startAngle: 0, sweep: 2 * Math.PI };
}

// ─── Params ─────────────────────────────────────────────────────────────

/** A key not used yet: `base`, `base2`, `base3`, … */
export function uniqueParamKey(spec: Spec, base: string): string {
  const clean = base.replace(/[^a-zA-Z0-9]/g, '').replace(/^[^a-zA-Z]+/, '') || 'param';
  const taken = new Set(spec.params.map((p) => p.key));
  if (!taken.has(clean)) return clean;
  for (let i = 2; ; i++) if (!taken.has(`${clean}${i}`)) return `${clean}${i}`;
}

export function addParam(spec: Spec, param: ObjectParam): Spec {
  return { ...spec, params: [...spec.params, param] };
}

const renameInBinding = (binding: NumberBinding | undefined, from: string, to: string): NumberBinding | undefined => (binding && 'param' in binding && binding.param === from ? { param: to } : binding);

function mapRepeats(spec: Spec, f: (repeat: ObjectRepeat, group: ObjectGroup) => ObjectRepeat): ObjectGroup[] {
  return spec.groups.map((g) => (g.repeat ? { ...g, repeat: f(g.repeat, g) } : g));
}

/** Every group's modifier bindings rewritten by `f`. */
function mapModifierBindings(groups: readonly ObjectGroup[], f: (binding: NumberBinding) => NumberBinding): ObjectGroup[] {
  return groups.map((g) => (g.modifiers ? { ...g, modifiers: g.modifiers.map((m): ObjectModifier => (m.kind === 'roundCorners' ? { ...m, radius: f(m.radius) } : m)) } : g));
}

/** Replace a param; a changed key is followed by every reference to it. */
export function updateParam(spec: Spec, key: string, next: ObjectParam): Spec {
  const params = spec.params.map((p) => (p.key === key ? next : p));
  if (next.key === key) return { ...spec, params };
  const groups = mapRepeats(spec, (r) =>
    r.kind === 'linear' ? { ...r, pitch: renameInBinding(r.pitch, key, next.key), count: renameInBinding(r.count, key, next.key) } : { ...r, count: renameInBinding(r.count, key, next.key) ?? r.count },
  ).map((g) => (g.visibleWhen?.param === key ? { ...g, visibleWhen: { ...g.visibleWhen, param: next.key } } : g));
  const renamed = renameInExpressions({ ...spec, params, groups: mapModifierBindings(groups, (b) => renameInBinding(b, key, next.key) ?? b) }, key, next.key);
  const rename = (k: string): string => (k === key ? next.key : k);
  return {
    ...renamed,
    ...(renamed.stepSyncs ? { stepSyncs: renamed.stepSyncs.map((x) => ({ ...x, step: rename(x.step), count: rename(x.count) })) } : {}),
    ...mapAssignmentParams(renamed, rename),
  };
}

// ─── Formulas ───────────────────────────────────────────────────────────

/** Every formula of the spec rewritten by `fn` (formulas, shape points, bindings, offsets, runs, handles, syncs). */
export function mapExpressions(spec: Spec, fn: (src: string) => string): Spec {
  const binding = (b: NumberBinding | undefined): NumberBinding | undefined => (b && 'expr' in b ? { expr: fn(b.expr) } : b);
  const offset = (o: NonNullable<ObjectGroup['offset']>): ObjectGroup['offset'] => ({
    ...(o.u !== undefined ? { u: fn(o.u) } : {}),
    ...(o.n !== undefined ? { n: fn(o.n) } : {}),
    ...(o.rotate !== undefined ? { rotate: fn(o.rotate) } : {}),
  });
  const assignments = (list: readonly ObjectParamAssignment[] | undefined) => list?.map((a) => ({ ...a, expr: fn(a.expr) }));
  const point = (p: ObjectHandlePoint): ObjectHandlePoint => (isFormulaHandlePoint(p) ? { u: fn(p.u), n: fn(p.n) } : p);
  return {
    ...spec,
    ...(spec.formulas ? { formulas: spec.formulas.map((f) => ({ ...f, expr: fn(f.expr) })) } : {}),
    shapes: spec.shapes.map((s) => (s.expr ? { ...s, expr: Object.fromEntries(Object.entries(s.expr).map(([field, src]) => [field, fn(src)])) } : s)),
    groups: spec.groups.map((g) => {
      const repeat: ObjectRepeat | undefined = !g.repeat
        ? undefined
        : g.repeat.kind === 'linear'
          ? { ...g.repeat, pitch: binding(g.repeat.pitch), count: binding(g.repeat.count), ...(g.repeat.runExpr ? { runExpr: { start: fn(g.repeat.runExpr.start), end: fn(g.repeat.runExpr.end) } } : {}) }
          : { ...g.repeat, count: binding(g.repeat.count) ?? g.repeat.count };
      return {
        ...g,
        ...(repeat ? { repeat } : {}),
        ...(g.offset ? { offset: offset(g.offset) } : {}),
        ...(g.visibleIf ? { visibleIf: fn(g.visibleIf) } : {}),
        ...(g.modifiers ? { modifiers: g.modifiers.map((m): ObjectModifier => (m.kind === 'roundCorners' ? { ...m, radius: binding(m.radius) ?? m.radius } : m)) } : {}),
      };
    }),
    ...(spec.handles
      ? {
          handles: {
            ...(spec.handles.start ? { start: point(spec.handles.start) } : {}),
            ...(spec.handles.end ? { end: point(spec.handles.end) } : {}),
            ...(spec.handles.depth ? { depth: spec.handles.depth.map((d) => ({ ...d, ...(d.at ? { at: { u: fn(d.at.u), n: fn(d.at.n) } } : {}), ...(d.assign ? { assign: assignments(d.assign) } : {}) })) } : {}),
          },
        }
      : {}),
    ...(spec.stepSyncs ? { stepSyncs: spec.stepSyncs.map((x) => ({ ...x, run: fn(x.run) })) } : {}),
    ...(spec.depthLink ? { depthLink: { expr: fn(spec.depthLink.expr), ...(spec.depthLink.onResize ? { onResize: assignments(spec.depthLink.onResize) } : {}) } } : {}),
  };
}

/** `from` → `to` as a NAME in every formula (not inside longer names, not function calls). */
export function renameInExpressions(spec: Spec, from: string, to: string): Spec {
  const pattern = new RegExp(`(?<![A-Za-z0-9_.])${from}(?![A-Za-z0-9_]|\\s*\\()`, 'g');
  return mapExpressions(spec, (src) => src.replace(pattern, to));
}

/** The params that assignments (handle drags, the depth link) write, renamed. */
function mapAssignmentParams(spec: Spec, rename: (key: string) => string): Partial<Spec> {
  const assignments = (list: readonly ObjectParamAssignment[] | undefined) => list?.map((a) => ({ ...a, param: rename(a.param) }));
  return {
    ...(spec.handles?.depth ? { handles: { ...spec.handles, depth: spec.handles.depth.map((d) => (d.assign ? { ...d, assign: assignments(d.assign) } : d)) } } : {}),
    ...(spec.depthLink?.onResize ? { depthLink: { ...spec.depthLink, onResize: assignments(spec.depthLink.onResize) } } : {}),
  };
}

/** Rename a formula and every reference to it. */
export function renameFormula(spec: Spec, from: string, to: string): Spec {
  const renamed = renameInExpressions(spec, from, to);
  return { ...renamed, ...(renamed.formulas ? { formulas: renamed.formulas.map((f) => (f.key === from ? { ...f, key: to } : f)) } : {}) };
}

/** Remove a param: bindings to it become its default value, conditions on it go. */
export function removeParam(spec: Spec, key: string): Spec {
  const param = spec.params.find((p) => p.key === key);
  if (!param) return spec;
  const fallback = typeof param.default === 'number' ? param.default : 1;
  const unbind = (b: NumberBinding | undefined): NumberBinding | undefined => (b && 'param' in b && b.param === key ? { value: fallback } : b);
  const groups = mapModifierBindings(
    mapRepeats(spec, (r) => (r.kind === 'linear' ? { ...r, pitch: unbind(r.pitch), count: unbind(r.count) } : { ...r, count: unbind(r.count) ?? r.count })).map((g) => {
      if (g.visibleWhen?.param !== key) return g;
      const { visibleWhen: _dropped, ...rest } = g;
      return rest;
    }),
    (b) => unbind(b) ?? b,
  );
  // Syncs and assignments of the param go too (formulas reading it are reported by the validator).
  const stepSyncs = spec.stepSyncs?.filter((x) => x.step !== key && x.count !== key);
  const withoutAssign = (list: readonly ObjectParamAssignment[] | undefined) => list?.filter((a) => a.param !== key);
  return {
    ...spec,
    params: spec.params.filter((p) => p.key !== key),
    groups,
    ...(spec.stepSyncs ? { stepSyncs } : {}),
    ...(spec.handles?.depth ? { handles: { ...spec.handles, depth: spec.handles.depth.map((d) => (d.assign ? { ...d, assign: withoutAssign(d.assign) } : d)) } } : {}),
    ...(spec.depthLink?.onResize ? { depthLink: { ...spec.depthLink, onResize: withoutAssign(spec.depthLink.onResize) } } : {}),
  };
}

// ─── Footprint ──────────────────────────────────────────────────────────

/**
 * Shift the whole drawing by (du, dn): shapes, repeat cells/runs and radial
 * centers, and the 3D model. The footprint stays centered on the origin, so
 * this is how one of its edges moves while the opposite one stays put
 * relative to the drawing. (Expression runs/offsets are formulas of the
 * footprint and are left alone.)
 */
export function shiftDrawing(spec: Spec, du: number, dn: number): Spec {
  if (du === 0 && dn === 0) return spec;
  const shiftPair = (pair: readonly [number, number], d: number): readonly [number, number] => [roundCoord(pair[0] + d), roundCoord(pair[1] + d)];
  return {
    ...spec,
    shapes: spec.shapes.map((s) => mapShape(s, (p) => [p[0] + du, p[1] + dn])),
    groups: spec.groups.map((g) => {
      if (!g.repeat) return g;
      if (g.repeat.kind === 'radial') return { ...g, repeat: { ...g.repeat, center: roundVec([g.repeat.center[0] + du, g.repeat.center[1] + dn]) } };
      // Index copies place themselves with formulas.
      if (g.repeat.kind === 'index') return g;
      const d = g.repeat.axis === 'u' ? du : dn;
      return { ...g, repeat: { ...g.repeat, cell: shiftPair(g.repeat.cell, d), ...(g.repeat.run ? { run: shiftPair(g.repeat.run, d) } : {}) } };
    }),
    model3d: spec.model3d ? { ...spec.model3d, position: [roundCoord(spec.model3d.position[0] + du), roundCoord(spec.model3d.position[1] + dn), spec.model3d.position[2]] } : spec.model3d,
  };
}

/** Footprint edges: `u+` = right (width), `n+` = top (depth), … */
export type FootprintEdge = 'u+' | 'u-' | 'n+' | 'n-';
export const MIN_FOOTPRINT = 0.01;

/**
 * Drag one footprint edge to `position` (m, along its axis) WITHOUT scaling
 * the drawing: the opposite edge keeps its place relative to the drawing,
 * which is re-centered by the returned `shift`. `symmetric` grows both
 * edges around the center instead (no shift).
 */
export function moveFootprintEdge(spec: Spec, edge: FootprintEdge, position: number, symmetric: boolean): { spec: Spec; shift: number } {
  const axis = edge[0] === 'u' ? 'width' : 'depth';
  const { size, center } = footprintEdgeResize(spec, edge, position, symmetric);
  const resized = { ...spec, defaultSize: { ...spec.defaultSize, [axis]: roundCoord(size) } };
  return { spec: axis === 'width' ? shiftDrawing(resized, -center, 0) : shiftDrawing(resized, 0, -center), shift: center };
}

/** The resized footprint's size along the edge's axis and its center in the CURRENT frame (see {@link moveFootprintEdge}). */
export function footprintEdgeResize(spec: Spec, edge: FootprintEdge, position: number, symmetric: boolean): { size: number; center: number } {
  const sign = edge[1] === '+' ? 1 : -1;
  const half = spec.defaultSize[edge[0] === 'u' ? 'width' : 'depth'] / 2;
  const size = symmetric ? Math.max(MIN_FOOTPRINT, 2 * Math.abs(position)) : Math.max(MIN_FOOTPRINT, sign * position + half);
  // The fixed edge sits at −sign·half.
  return { size, center: symmetric ? 0 : roundCoord(-sign * half + (sign * size) / 2) };
}

/** Footprint = the bounds of the visible drawing, re-centered on it. Null when there is nothing to fit. */
export function fitFootprintToDrawing(spec: Spec): Spec | null {
  const visible = spec.shapes.filter((s) => !s.hidden);
  if (visible.length === 0) return null;
  const bu = shapesBounds(visible, 0);
  const bn = shapesBounds(visible, 1);
  const width = roundCoord(Math.max(MIN_FOOTPRINT, bu.max - bu.min));
  const depth = roundCoord(Math.max(MIN_FOOTPRINT, bn.max - bn.min));
  return shiftDrawing({ ...spec, defaultSize: { ...spec.defaultSize, width, depth } }, roundCoord(-(bu.min + bu.max) / 2), roundCoord(-(bn.min + bn.max) / 2));
}

// ─── Transform (2D gizmo) ───────────────────────────────────────────────

/** A shape as a closed/open polyline (for transforms a rect/circle/arc can't express). */
function toPolyline(shape: ObjectShape): ObjectShape {
  const polys = tessellateObjectShape(shape);
  const points = polys[0]?.points ?? [];
  const closed = shape.kind === 'rect' || shape.kind === 'circle';
  return { id: shape.id, kind: 'polyline', points: closed ? points.slice(0, -1) : points, closed, ...(shape.name ? { name: shape.name } : {}), ...(shape.style ? { style: shape.style } : {}), ...(shape.layer ? { layer: shape.layer } : {}), ...(shape.hidden ? { hidden: shape.hidden } : {}) };
}

/**
 * Scale shapes by (su, sn) about `origin`. Uniform scales keep circles and
 * arcs; a non-uniform one turns them into polylines (a stretched circle is an
 * ellipse). Negative factors mirror.
 */
export function scaleShapes(spec: Spec, ids: readonly string[], origin: Vec2, su: number, sn: number): Spec {
  const f = (p: Vec2): Vec2 => [origin[0] + (p[0] - origin[0]) * su, origin[1] + (p[1] - origin[1]) * sn];
  const uniform = Math.abs(Math.abs(su) - Math.abs(sn)) < 1e-9;
  return {
    ...spec,
    shapes: spec.shapes.map((s) => {
      if (!ids.includes(s.id)) return s;
      if ((s.kind === 'circle' || s.kind === 'arc') && !uniform) return mapShape(toPolyline(s), f);
      const mapped = mapShape(s, f, Math.abs(su));
      if (mapped.kind !== 'arc' || (su > 0 && sn > 0)) return mapped;
      // A mirror flips the arc: map its angles through the reflection.
      const reflect = (a: number): number => Math.atan2(Math.sin(a) * Math.sign(sn), Math.cos(a) * Math.sign(su));
      return { ...mapped, startAngle: reflect(s.kind === 'arc' ? s.startAngle : 0), endAngle: reflect(s.kind === 'arc' ? s.endAngle : 0) };
    }),
  };
}

/**
 * Rotate shapes by `angle` (radians, counter-clockwise) about `center`.
 * Rectangles become closed polylines unless the turn is a multiple of 90°.
 */
export function rotateShapes(spec: Spec, ids: readonly string[], center: Vec2, angle: number): Spec {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const f = (p: Vec2): Vec2 => [center[0] + (p[0] - center[0]) * cos - (p[1] - center[1]) * sin, center[1] + (p[0] - center[0]) * sin + (p[1] - center[1]) * cos];
  const quarterTurn = Math.abs(Math.round(angle / (Math.PI / 2)) * (Math.PI / 2) - angle) < 1e-9;
  return {
    ...spec,
    shapes: spec.shapes.map((s) => {
      if (!ids.includes(s.id)) return s;
      if (s.kind === 'rect' && !quarterTurn) return mapShape(toPolyline(s), f);
      const mapped = mapShape(s, f);
      return mapped.kind === 'arc' && s.kind === 'arc' ? { ...mapped, startAngle: s.startAngle + angle, endAngle: s.endAngle + angle } : mapped;
    }),
  };
}
